import React from 'react';
import {Box, Button, Divider, makeStyles, Typography} from '@material-ui/core';
import {Add} from '@material-ui/icons';
import {
    AdditionalDocsRequiredEnum,
    I9DocumentGroupsEnum,
    useUploadDocumentContext
} from '@swipejobs/i9-document-validations-react';
import {LoadingButton} from '@swipejobs/fe-service-desktop-ui-components';
import {flatten, lte, map} from 'lodash-es';
import DocumentUploadModalV1 from './DocumentUploadModalV1';
import UploadedDocumentPreview from './UploadedDocumentPreview';
import {cacheTranslations} from '@utils/cache-translations';
import {createTranslationService} from '@services/factories';
import {observer} from 'mobx-react';
import {ISection2DocumentUploadStore} from '@stores/onboarding-review/section2-document-upload-store';
import useSaveDocumentUpload from '@hooks/useSaveDocumentUpload';
import {OnConfirmType} from '@models/onConfirm-type';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: 5,
    },
    header: {
        flexDirection: 'column',
        marginTop: 10,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height: '100%',
        marginBottom: theme.spacing(2),
    },
    scrollContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'auto',
        padding: 5,
    },
    title: {
        marginBottom: theme.spacing(2),
        fontWeight: 'bold',
        fontSize: 'large',
    },
    divider: {
        height: 5,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    seeInstructions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 8,
        marginBottom: 16,
        fontWeight: 'bold',
        color: '#106ba3',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    solidDivider: {
        width: '100%',
        borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
    },
    buttons: {
        display: 'flex',
        flexDirection: 'row',
        gap: '24px',
        alignItems: 'center',
    },
}));

export enum UploadProgressEnum {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    DONE = 'DONE',
    EDITING = 'EDITING',
}

enum Resources {
    sectionTitle = 'sectionTitle',
    sectionSubTitle = 'sectionSubTitle',
    addDocument = 'addDocument',
    document1Title = 'document1Title',
    document2Title = 'document2Title',
    I9_LIST_A = 'I9_LIST_A',
    I9_LIST_B = 'I9_LIST_B',
    I9_LIST_C = 'I9_LIST_C',
    uploadDocuments = 'uploadDocuments',
    noImagePlaceholder = 'noImagePlaceholder',
    listOfDocuments = 'listOfDocuments',
    noDocumentsInfo = 'noDocumentsInfo',
    needListAorListB = 'needListAorListB',
    needListB = 'needListB',
    needListC = 'needListC',
}

interface Props {
    store: ISection2DocumentUploadStore;
    onConfirm: OnConfirmType;
    onUseOtherDocuments?: () => void;
    isReceiptUpload?: boolean;
}

const I9DocumentUploadV1 = ({store, onConfirm, isReceiptUpload, onUseOtherDocuments}: Props) => {
    const css = useStyles();

    const oldResources = cacheTranslations(
        createTranslationService(),
        {
            continueButton: 'continueButton',
            resetButton: 'resetButton'
        },
        'i9DocumentUpload'
    );

    const resources = cacheTranslations(
        createTranslationService(),
        Resources,
        'i9DocumentUpload.uploadSingleDocumentPort'
    );

    const additionalDocumentsRequirementTitles = cacheTranslations(
        createTranslationService(),
        AdditionalDocsRequiredEnum,
        'i9DocumentUpload.additionalDocumentsTitles'
    );

    const {
        handleAddInitialDocument,
        isEnableContinueButton,
        resetAllUploadProgress,
        savedDocuments,
        getNextDocumentOptions,
        handleRemoveCurrentUploadingDocument,
    } = useUploadDocumentContext();

    const { handleUploadSavedDocuments } = useSaveDocumentUpload(store, onConfirm);

    const {I9_LIST_A, I9_LIST_B, I9_LIST_C} = I9DocumentGroupsEnum;

    const getSectionTitle = () => {
        const {options, isAdditionalDocument, requiredLimit, requiredOption} =
            getNextDocumentOptions();
        const documentGroups = flatten(map(options, (i) => i.documentGroups));
        const isListA = documentGroups.includes(I9_LIST_A);
        const isListB = documentGroups.includes(I9_LIST_B);
        const isListC = documentGroups.includes(I9_LIST_C);
        if (lte(requiredLimit, 0)) return '';
        if (isAdditionalDocument && requiredOption) return additionalDocumentsRequirementTitles.EQ;
        if (isListA) return resources.needListAorListB;
        if (isListB) return resources.needListB;
        if (isListC) return resources.needListC;
        return 'Select a document';
    };

    const visibleSummaryOfDocs = savedDocuments.filter((i) =>
        [UploadProgressEnum.DONE, UploadProgressEnum.EDITING].includes(i.progress),
    );

    const isOpenUploadDocModal = !!savedDocuments.find((i) => i.progress !== UploadProgressEnum.DONE);

    return (
        <Box>
            <Typography style={{margin: '24px 0'}}>{resources.listOfDocuments}</Typography>
            {visibleSummaryOfDocs.length === 0 ? (
                <Typography style={{margin: '16px 0'}}>{resources.noDocumentsInfo}</Typography>
            ) : (
                <UploadedDocumentPreview/>
            )}
            {savedDocuments.every((i) => i.progress === UploadProgressEnum.DONE) &&
                getNextDocumentOptions().options.length > 0 && (
                    <Box>
                        <Typography style={{margin: '8px 0'}}>{getSectionTitle()}</Typography>
                        <Button
                            variant='contained'
                            onClick={() => handleAddInitialDocument()}
                            startIcon={<Add/>}
                            color='primary'
                        >
                            {resources.addDocument}
                        </Button>
                    </Box>
                )}
            <Divider style={{margin: '16px 0'}}/>
            <Box className={css.buttons}>
                <LoadingButton
                    variant='contained'
                    color='primary'
                    onClick={handleUploadSavedDocuments}
                    disabled={!isEnableContinueButton()}
                >
                    {oldResources.continueButton}
                </LoadingButton>
                {savedDocuments.length >= 1 && (
                    <Button variant='outlined' color='primary' onClick={resetAllUploadProgress}>
                        {oldResources.resetButton}
                    </Button>
                )}
            </Box>
            {isOpenUploadDocModal && (
                <DocumentUploadModalV1
                    open={isOpenUploadDocModal}
                    onClose={() => handleRemoveCurrentUploadingDocument()}
                    i9Section2Status={
                        // @ts-ignore
                        store?.i9StatusDetails?.i9Section2Status ?? ''}
                    handleArchiveReceipts={onUseOtherDocuments}
                />
            )}
        </Box>
    );
};

export default observer(I9DocumentUploadV1);



// WEBPACK FOOTER //
// ./src/screens/review-section2/document-upload/v2/I9DocumentUploadV1.tsx